import Head from "next/head";
import type { SeoMeta } from "@/interfaces/seo";
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  buildCanonicalUrl,
  getRobotsValueForPath,
} from "@/utils/seo";

interface SeoHeadProps {
  meta: SeoMeta;
}

export default function SeoHead({ meta }: SeoHeadProps) {
  const canonicalUrl = buildCanonicalUrl(meta.canonicalPath);
  const robots = meta.robots || getRobotsValueForPath(meta.canonicalPath);
  const image = meta.ogImage || DEFAULT_OG_IMAGE;
  const schema = meta.schema
    ? Array.isArray(meta.schema)
      ? meta.schema
      : [meta.schema]
    : [];

  return (
    <Head>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={robots} />

      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={image} />

      {schema.map((item, index) => (
        <script
          key={`seo-schema-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(item) }}
        />
      ))}
    </Head>
  );
}
